import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Ticket, Calendar, MapPin, Loader2, CheckCircle2, QrCode, XCircle, Tag, Repeat, Heart, X } from 'lucide-react';

export default function MyTickets() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [donateFor, setDonateFor] = useState(null);   // ticket object OR null
  const [donateChoice, setDonateChoice] = useState('keep');

  const load = () => evtApi.myTickets().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const cancel = async (t) => {
    if (!window.confirm(`Cancel your free ticket #${t.ticketId}? Your +100 loyalty points will be refunded.`)) return;
    setBusyId(t.id);
    try {
      await evtApi.cancelTicket(t.ticketId);
      toast({ title: 'Ticket cancelled', description: 'Loyalty points refunded.' });
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.useResale) {
        toast({ title: 'Paid tickets can\'t be refunded', description: 'Redirecting to resale…' });
        return listResale(t);
      }
      toast({ title: 'Cancel failed', description: typeof d === 'string' ? d : 'Try again' });
    } finally { setBusyId(null); }
  };

  const listResale = (t) => {
    setDonateChoice('keep');
    setDonateFor(t);
  };

  const confirmListResale = async () => {
    const t = donateFor;
    if (!t) return;
    setBusyId(t.id);
    try {
      const r = await evtApi.listResale(t.ticketId, donateChoice);
      const donateNote = donateChoice === 'community_fund'
        ? ' Net proceeds will go to the Community Fund.'
        : donateChoice === 'youth_fund' ? ' Net proceeds will go to the Youth Fund.' : '';
      toast({
        title: 'Ticket listed for resale',
        description: (r.promotedWaitlist ? 'A waitlisted attendee has 48h to claim.' : 'Now available to the public at 25% off.') + donateNote,
      });
      setDonateFor(null);
      await load();
    } catch (err) {
      toast({ title: 'Listing failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusyId(null); }
  };

  const withdrawResale = async (t) => {
    if (!window.confirm(`Pull ticket #${t.ticketId} off the resale market?`)) return;
    setBusyId(t.id);
    try {
      await evtApi.withdrawResale(t.ticketId);
      toast({ title: 'Resale listing withdrawn' });
      await load();
    } catch (err) {
      toast({ title: 'Withdraw failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusyId(null); }
  };

  if (rows === null) return (
    <div className="flex items-center gap-2 text-gray-400" data-testid="my-tickets-loading">
      <Loader2 className="animate-spin" size={16}/> Loading tickets…
    </div>
  );

  return (
    <div className="space-y-6" data-testid="my-tickets-page">
      <div>
        <h1 className="text-2xl font-bold">My Tickets</h1>
        <p className="text-gray-400 text-sm">Show the QR at the door to check in.</p>
      </div>

      {rows.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="my-tickets-empty">
          <Ticket className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold text-white">No tickets yet</div>
          <p className="text-sm text-gray-400 mt-1">Reserve a free ticket or buy one to see it here.</p>
          <Link to="/events" className="pbb-btn-green mt-5 inline-flex max-w-xs">Browse events</Link>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="my-tickets-list">
          {rows.map(t => {
            const ev = t.event || {};
            const paid = t.purchaseType === 'paid';
            const checked = !!t.checkedInAt;
            const cancelled = t.status === 'cancelled' || !!t.cancelledAt;
            const listed = t.status === 'listed_for_resale' || !!t.resaleListingId;
            const resold = t.status === 'resold';
            const busy = busyId === t.id;
            return (
              <li key={t.id} className="pbb-card p-4 flex items-start gap-4 flex-wrap sm:flex-nowrap" data-testid={`ticket-row-${t.ticketId}`}>
                {(!cancelled && !resold && !listed) ? (
                  <img src={evtApi.qrUrl(t.ticketId)} alt="Ticket QR"
                    className="w-28 h-28 rounded-lg border border-[#2a2a2a] bg-white shrink-0"/>
                ) : (
                  <div className="w-28 h-28 rounded-lg border border-[#2a2a2a] bg-black flex items-center justify-center text-gray-500 shrink-0"
                    title={listed ? 'QR hidden while ticket is listed for resale — withdraw to see it again.'
                                    : cancelled ? 'Ticket cancelled' : 'Ticket resold'}>
                    {listed ? <Tag size={30}/> : <XCircle size={30}/>}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-white truncate">{ev.title || t.eventTitle || 'Event'}</div>
                    {cancelled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">CANCELLED</span>}
                    {resold && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/40">RESOLD</span>}
                    {!cancelled && !resold && listed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">LISTED FOR RESALE</span>}
                    {checked && !cancelled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/40 inline-flex items-center gap-1"><CheckCircle2 size={10}/> CHECKED IN</span>}
                    {!checked && !cancelled && !resold && !listed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">READY</span>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${paid ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' : 'bg-gray-500/20 text-gray-300 border-gray-500/40'}`}>
                      {paid ? `PAID · $${(t.priceUsd || 0).toFixed(2)}` : 'FREE'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-3 mt-1 flex-wrap">
                    {ev.date && <span className="inline-flex items-center gap-1"><Calendar size={11}/> {ev.date}{ev.time ? ` · ${ev.time}` : ''}</span>}
                    {(ev.location || ev.city) && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {[ev.location, ev.city].filter(Boolean).join(', ')}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">Ticket ID</div>
                  <div className="font-mono font-bold text-white tracking-widest text-sm">#{t.ticketId}</div>
                  {t.attendeeName && t.attendeeName.trim().toLowerCase() !== (t.userName || '').trim().toLowerCase() && (
                    <div className="text-[11px] text-gray-400 mt-1" data-testid={`ticket-attendee-${t.ticketId}`}>
                      Attendee: <span className="text-white font-semibold">{t.attendeeName}</span>
                    </div>
                  )}
                  {checked && !cancelled && (
                    <div className="text-[11px] text-green-400 mt-1">Checked in {new Date(t.checkedInAt).toLocaleString()}</div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {!cancelled && !resold && (
                      <Link to={`/tickets/${t.ticketId}`} data-testid={`ticket-view-${t.ticketId}`}
                        className="text-xs px-2.5 py-1 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-200 inline-flex items-center gap-1">
                        <QrCode size={12}/> Full ticket
                      </Link>
                    )}
                    {!cancelled && !resold && !checked && !listed && !paid && (
                      <button onClick={() => cancel(t)} disabled={busy}
                        data-testid={`ticket-cancel-${t.ticketId}`}
                        className="text-xs px-2.5 py-1 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1 disabled:opacity-60">
                        <XCircle size={12}/> Cancel (refund points)
                      </button>
                    )}
                    {!cancelled && !resold && !checked && !listed && paid && (
                      <button onClick={() => listResale(t)} disabled={busy}
                        data-testid={`ticket-resell-${t.ticketId}`}
                        className="text-xs px-2.5 py-1 rounded-lg border border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10 inline-flex items-center gap-1 disabled:opacity-60">
                        <Tag size={12}/> List for resale
                      </button>
                    )}
                    {listed && !resold && (
                      <button onClick={() => withdrawResale(t)} disabled={busy}
                        data-testid={`ticket-withdraw-${t.ticketId}`}
                        className="text-xs px-2.5 py-1 rounded-lg border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 text-gray-200 inline-flex items-center gap-1 disabled:opacity-60">
                        <Repeat size={12}/> Withdraw resale
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {donateFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="resale-donate-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setDonateFor(null); }}>
          <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-2xl max-w-md w-full p-5">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-white font-bold text-lg">List ticket for resale</div>
                <div className="text-xs text-gray-400">#{donateFor.ticketId} · {donateFor.event?.title || donateFor.eventTitle}</div>
              </div>
              <button type="button" onClick={() => setDonateFor(null)} data-testid="resale-donate-close"
                className="text-gray-400 hover:text-white"><X size={18}/></button>
            </div>
            <div className="text-xs text-gray-300 mt-2">
              Waitlisted attendees get first offer at the original <b>${(donateFor.priceUsd || 0).toFixed(2)}</b> for 48h.
              If unclaimed, it goes public at <b>25% off</b>. A 10% platform fee is deducted from every sale.
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold text-white mb-2 inline-flex items-center gap-1"><Heart size={12} className="text-pink-400"/> Donate my net proceeds? (Optional)</div>
              <div className="grid grid-cols-1 gap-2">
                <DonateOption value="keep" current={donateChoice} onChange={setDonateChoice}
                  title="Keep the money" subtitle="Net proceeds go to your PBB wallet"/>
                <DonateOption value="community_fund" current={donateChoice} onChange={setDonateChoice}
                  title="Donate to Community Fund" subtitle="Fully fund community programs"/>
                <DonateOption value="youth_fund" current={donateChoice} onChange={setDonateChoice}
                  title="Donate to Youth Fund" subtitle="Support scholarships and youth events"/>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setDonateFor(null)} data-testid="resale-donate-cancel"
                className="flex-1 px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-gray-500 text-gray-200 text-sm">
                Cancel
              </button>
              <button type="button" onClick={confirmListResale} disabled={busyId === donateFor.id}
                data-testid="resale-donate-confirm"
                className="flex-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60">
                {busyId === donateFor.id ? <Loader2 size={14} className="animate-spin"/> : <Tag size={14}/>} List for resale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DonateOption({ value, current, onChange, title, subtitle }) {
  const active = current === value;
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${active ? 'border-green-500 bg-green-500/10' : 'border-[#2a2a2a] hover:border-gray-500'}`}
      data-testid={`resale-donate-option-${value}`}>
      <input type="radio" name="donateChoice" value={value} checked={active}
        onChange={() => onChange(value)} className="mt-0.5 accent-green-500"/>
      <div className="flex-1">
        <div className={`text-sm font-semibold ${active ? 'text-white' : 'text-gray-200'}`}>{title}</div>
        <div className="text-[11px] text-gray-400">{subtitle}</div>
      </div>
    </label>
  );
}
